/* Pagina pubblica del Fascicolo Privacy — accessibile tramite magic link via email.
 * URL: /fascicolo?token=XYZ
 * Non richiede login. L'utente vede SOLO il proprio fascicolo + i fascicoli dei
 * trattamenti a cui è autorizzato.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicFascicoloPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!token) {
      setErr("Link non valido: token mancante.");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/fascicolo-pubblico/${encodeURIComponent(token)}`);
        setData(r.data);
      } catch (e) {
        setErr(e.response?.data?.detail || "Errore caricamento fascicolo");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return (
      <CenteredCard>
        <div style={{ fontSize: 14, color: "#666" }}>⏳ Caricamento del fascicolo...</div>
      </CenteredCard>
    );
  }
  if (err) {
    return (
      <CenteredCard>
        <div style={{ background: "#c62828", color: "white", padding: "14px 18px", fontWeight: 700, fontSize: 14, marginBottom: 14, borderRadius: 4 }}>
          ⚠ {err}
        </div>
        <div style={{ fontSize: 12, color: "#666" }}>
          Se il problema persiste contatta la segreteria.
        </div>
      </CenteredCard>
    );
  }

  const { docente, fascicolo_docente, fascicoli_trattamenti } = data || {};
  const totDocDocs = (fascicolo_docente?.documenti || []).length;
  const totTratt = (fascicoli_trattamenti || []).length;
  const totTrattDocs = (fascicoli_trattamenti || []).reduce((a, t) => a + (t.documenti?.length || 0), 0);

  return (
    <div style={{ background: "#0a2342", minHeight: "100vh", paddingBottom: 60 }}>
      <header style={{ background: "linear-gradient(135deg, #0a2342, #1c3d6e)", padding: "30px 24px", color: "white", borderBottom: "3px solid #ef6c00" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: 0.5 }}>
            canossa<span style={{ color: "#ffd54f" }}>HUB</span>
          </div>
          <div style={{ fontSize: 13, opacity: 0.85, marginTop: 4 }}>Fascicolo Privacy · Istituto Canossiano</div>
        </div>
      </header>

      <div style={{ maxWidth: 1100, margin: "20px auto", padding: "0 24px" }}>
        {/* Card anagrafica docente */}
        <div style={{ background: "white", padding: 22, borderRadius: 6, marginBottom: 18, boxShadow: "0 2px 12px rgba(0,0,0,0.15)" }}>
          <div style={{ fontSize: 12, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Fascicolo privacy intestato a</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#0a2342" }}>{docente?.cognome} {docente?.nome}</div>
          <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
            Codice docente: <b>{docente?.codice}</b>
            {docente?.codice_fiscale && <> · CF: <b>{docente.codice_fiscale}</b></>}
            {docente?.email && <> · Email: <b>{docente.email}</b></>}
          </div>
          <div style={{ display: "flex", gap: 20, marginTop: 14, paddingTop: 14, borderTop: "1px solid #eee" }}>
            <Stat icon="👤" label="Documenti del docente" value={totDocDocs} color="#6a1b9a" />
            <Stat icon="📋" label="Trattamenti autorizzati" value={totTratt} color="#1565c0" />
            <Stat icon="📄" label="Documenti dei trattamenti" value={totTrattDocs} color="#2e7d32" />
          </div>
        </div>

        {/* Sezione 1: fascicolo docente */}
        <Section title="👤 I tuoi documenti" color="#6a1b9a"
          subtitle={fascicolo_docente?.generato_il ? `Generato il ${new Date(fascicolo_docente.generato_il).toLocaleDateString("it-IT")}` : "Nessun documento personale generato"}>
          {totDocDocs === 0 ? (
            <Empty msg="Non sono ancora stati generati documenti personali. La segreteria provvederà a generarli e ti avviserà." />
          ) : (
            <DocList docs={fascicolo_docente.documenti}
              buildUrl={(d) => `${API_URL}/api/fascicolo-pubblico/${encodeURIComponent(token)}/download/docente/${encodeURIComponent(docente.codice)}/${encodeURIComponent(d.modello_codice)}`} />
          )}
        </Section>

        {/* Sezione 2: trattamenti autorizzati */}
        <Section title="📋 Trattamenti a cui sei autorizzato" color="#1565c0"
          subtitle={totTratt === 0 ? "Nessuna autorizzazione presente." : `${totTratt} ${totTratt === 1 ? "trattamento" : "trattamenti"}`}>
          {totTratt === 0 ? (
            <Empty msg="Non risulti autorizzato ad alcun trattamento privacy." />
          ) : (
            fascicoli_trattamenti.map(t => (
              <div key={t.codice} style={{ background: "#fafafa", border: "1px solid #e0e0e0", borderRadius: 4, padding: 14, marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
                  <span style={{ fontFamily: "Consolas, monospace", fontWeight: 700, color: "#0d47a1", fontSize: 13 }}>{t.codice}</span>
                  <span style={{ fontSize: 15, fontWeight: 700, color: "#37474f" }}>{t.nome}</span>
                  <span style={{ marginLeft: "auto", fontSize: 11, color: "#888" }}>
                    {t.documenti.length === 0 ? "— fascicolo non ancora generato" :
                      `${t.documenti.length} documenti · ${t.generato_il ? new Date(t.generato_il).toLocaleDateString("it-IT") : ""}`}
                  </span>
                </div>
                {t.documenti.length > 0 && (
                  <DocList docs={t.documenti}
                    buildUrl={(d) => `${API_URL}/api/fascicolo-pubblico/${encodeURIComponent(token)}/download/trattamento/${encodeURIComponent(t.codice)}/${encodeURIComponent(d.modello_codice)}`} />
                )}
              </div>
            ))
          )}
        </Section>

        <div style={{ marginTop: 30, padding: 14, background: "rgba(255,255,255,0.06)", color: "#cfd8dc", fontSize: 11, textAlign: "center", borderRadius: 4 }}>
          Questo è il tuo accesso personale. Non condividere il link con altri. In caso di dubbi contatta la segreteria all'indirizzo segreteria@canossadiaz.it.
        </div>
      </div>
    </div>
  );
}

function CenteredCard({ children }) {
  return (
    <div style={{ background: "#0a2342", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 30 }}>
      <div style={{ background: "white", padding: 30, borderRadius: 6, maxWidth: 480, boxShadow: "0 4px 20px rgba(0,0,0,0.3)" }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: "#0a2342", marginBottom: 14 }}>canossaHUB · Fascicolo Privacy</div>
        {children}
      </div>
    </div>
  );
}

function Section({ title, subtitle, color, children }) {
  return (
    <div style={{ background: "white", borderRadius: 6, marginBottom: 18, boxShadow: "0 2px 8px rgba(0,0,0,0.1)", overflow: "hidden" }}>
      <div style={{ background: color, color: "white", padding: "10px 16px" }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>{title}</div>
        {subtitle && <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>{subtitle}</div>}
      </div>
      <div style={{ padding: 14 }}>{children}</div>
    </div>
  );
}

function Stat({ icon, label, value, color }) {
  return (
    <div style={{ flex: 1, padding: 10, background: "#fafafa", borderLeft: `3px solid ${color}`, borderRadius: 2 }}>
      <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 0.4 }}>{icon} {label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color, marginTop: 4 }}>{value}</div>
    </div>
  );
}

function Empty({ msg }) {
  return <div style={{ padding: 20, textAlign: "center", color: "#888", fontSize: 13, fontStyle: "italic" }}>{msg}</div>;
}

function DocList({ docs, buildUrl }) {
  return (
    <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
      <thead style={{ background: "#f5f5f5" }}>
        <tr>
          <th style={{ width: 36, padding: 6, textAlign: "center" }}>#</th>
          <th style={{ padding: 6, textAlign: "left" }}>NOME DOCUMENTO</th>
          <th style={{ width: 90, padding: 6, textAlign: "right" }}>DIMENSIONE</th>
          <th style={{ width: 200, padding: 6, textAlign: "center" }}>AZIONI</th>
        </tr>
      </thead>
      <tbody>
        {docs.map((d, i) => (
          <tr key={d.modello_codice} style={{ borderTop: "1px solid #eee" }}>
            <td style={{ textAlign: "center", color: "#888", padding: 6 }}>{i + 1}</td>
            <td style={{ padding: 6 }}>
              <div style={{ fontWeight: 600 }}>{d.modello_nome || d.modello_codice}</div>
              <div style={{ fontSize: 10, color: "#888", fontFamily: "Consolas, monospace" }}>{d.filename}</div>
            </td>
            <td style={{ textAlign: "right", padding: 6, color: "#666", fontSize: 11 }}>
              {d.size ? `${Math.round(d.size / 1024)} KB` : "—"}
            </td>
            <td style={{ textAlign: "center", padding: 6, whiteSpace: "nowrap" }}>
              <a href={`${buildUrl(d)}?inline=1`} target="_blank" rel="noopener noreferrer"
                style={{ display: "inline-block", background: "#6a1b9a", color: "white", textDecoration: "none", padding: "4px 10px", fontSize: 11, fontWeight: 700, marginRight: 4, borderRadius: 2 }}>
                👁 Anteprima
              </a>
              <a href={buildUrl(d)}
                style={{ display: "inline-block", background: "#1565c0", color: "white", textDecoration: "none", padding: "4px 10px", fontSize: 11, fontWeight: 700, borderRadius: 2 }}>
                📄 Scarica
              </a>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
